import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { MediaCalculatorUser, AccessRoles } from 'shared/auth';

interface AuthContextType {
  userRoles: AccessRoles[];
  rolesLoaded: boolean;
}

const AuthContext = createContext<AuthContextType>({
  userRoles: [],
  rolesLoaded: false,
});

export const isAllowed = (allowedRoles: AccessRoles[], roles: AccessRoles[]) =>
  roles.some((role) => allowedRoles.includes(role));

export function AuthProvider({ children }: React.PropsWithChildren) {
  const { user, isLoading, isAuthenticated } = useAuth0<MediaCalculatorUser>();
  const [userRoles, setUserRoles] = useState<AccessRoles[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setUserRoles([]);
      setRolesLoaded(true);
      return;
    }

    setUserRoles(user?.['media-calculator/roles'] || []);
    setRolesLoaded(true);
  }, [user, isLoading, isAuthenticated]);

  const value = useMemo(
    () => ({ userRoles, rolesLoaded }),
    [userRoles, rolesLoaded]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthContext = () => useContext(AuthContext);
